import { translateColumnDisplayName, translateOptionDisplayName } from '../components/shared/filters/filterDisplayHelpers';
import { FILTER_OPERATORS } from './filterConstants';

/**
 * Value-first filter suggestions.
 *
 * The filter menu used to answer "which field are you filtering on?". This
 * answers "which value are you looking for?" — the same question Linear asks —
 * by matching a query against the values of every field at once and returning
 * flat "<Field> > <Value>" rows.
 *
 * Pure module: no React, no network. The counts come from the caller.
 */

// Columns whose values are enumerable and worth matching against. A numeric or
// date column has no value list to search — those stay field-first.
const MATCHABLE_TYPES = ['string', 'id', undefined];

// Rank buckets. A value that starts with what you typed beats one that merely
// contains it, however many movements sit behind each.
const RANK_EXACT = 0;
const RANK_PREFIX = 1;
const RANK_WORD = 2;
const RANK_CONTAINS = 3;

const norm = (value) => String(value ?? '')
  .toLowerCase()
  .normalize('NFD')
  // Spanish is written with accents and searched without them: "categoría"
  // has to be reachable by typing "categoria".
  .replace(/[̀-ͯ]/g, '')
  .trim();

const rankOf = (haystack, needle) => {
  if (!haystack) return null;
  if (haystack === needle) return RANK_EXACT;
  if (haystack.startsWith(needle)) return RANK_PREFIX;
  const idx = haystack.indexOf(needle);
  if (idx < 0) return null;
  // Matching the start of any word reads as intentional; matching mid-word is
  // the weakest signal we still show.
  return /\s/.test(haystack.charAt(idx - 1)) ? RANK_WORD : RANK_CONTAINS;
};

export const suggestionId = (entity, column, value) => `${entity}.${column}:${value}`;

/**
 * Default operator for a value row. Everything enumerable is an "is one of",
 * which is what lets a second pick merge into the chip the first one created.
 */
const operatorFor = (filter) => (
  filter.columnType === 'numeric' ? FILTER_OPERATORS.GTE : FILTER_OPERATORS.IN
);

/**
 * Build the ranked value rows for a query.
 *
 * @param {Array} filterOptions - FilterDefinition[] from /movements/filter-options
 * @param {string} query - what the user typed
 * @param {Object} [opts]
 * @param {Object} [opts.counts] - { [suggestionId]: number } from the server
 * @param {number} [opts.limit=8] - rows to return
 * @param {number} [opts.perField=3] - rows one field may contribute
 * @returns {Array} suggestion rows, each carrying the condition it would apply
 */
export function buildValueSuggestions(filterOptions = [], query = '', { counts = {}, limit = 8, perField = 3 } = {}) {
  const needle = norm(query);
  if (!needle) return [];

  const rows = [];

  (filterOptions || []).forEach((filter) => {
    if (!MATCHABLE_TYPES.includes(filter.columnType)) return;
    const fieldLabel = translateColumnDisplayName(filter.entity, filter.column, filter.displayName);

    (filter.options || []).forEach((option) => {
      const valueLabel = translateOptionDisplayName(
        filter.entity, filter.column, option.value, option.displayName
      );
      // Both labels are searched: the localized one the user reads, and the raw
      // English the backend sent. Without the first, a Spanish user typing
      // "Ingreso" or "Sin extracto" matches nothing.
      const rank = Math.min(
        rankOf(norm(valueLabel), needle) ?? Infinity,
        rankOf(norm(option.displayName), needle) ?? Infinity,
        // A value is also reachable through its field: "categoria coffee".
        rankOf(norm(option.displaySubName), needle) ?? Infinity
      );
      if (!Number.isFinite(rank)) return;

      const id = suggestionId(filter.entity, filter.column, option.value);
      rows.push({
        id,
        entity: filter.entity,
        column: filter.column,
        value: option.value,
        fieldLabel,
        valueLabel,
        subLabel: option.displaySubName || null,
        icon: option.icon || null,
        iconUrl: option.iconUrl || null,
        isActive: option.isActive,
        count: counts[id] ?? null,
        rank,
        condition: {
          entity: filter.entity,
          column: filter.column,
          operator: operatorFor(filter),
          values: [option.value],
        },
      });
    });
  });

  rows.sort((a, b) => (
    a.rank - b.rank
    // A known count outranks an unknown one, so rows settle rather than jump
    // when the server's numbers arrive.
    || (b.count ?? -1) - (a.count ?? -1)
    || a.valueLabel.localeCompare(b.valueLabel)
  ));

  // No single field may flood the list — three accounts must not push every
  // category off the bottom.
  const perFieldCount = {};
  const kept = [];
  for (const row of rows) {
    const key = `${row.entity}.${row.column}`;
    if ((perFieldCount[key] || 0) >= perField) continue;
    perFieldCount[key] = (perFieldCount[key] || 0) + 1;
    kept.push(row);
    if (kept.length >= limit) break;
  }
  return kept;
}

/**
 * Fields whose *name* matches the query — the old field-first list, kept as the
 * tail of the menu so drilling into a submenu is still possible.
 */
export function matchFields(filterOptions = [], query = '') {
  const needle = norm(query);
  if (!needle) return filterOptions || [];
  return (filterOptions || []).filter((filter) => {
    const localized = translateColumnDisplayName(filter.entity, filter.column, filter.displayName || '');
    return rankOf(norm(localized), needle) !== null
      || rankOf(norm(filter.displayName), needle) !== null;
  });
}

/**
 * Merge server suggestions into the local rows: counts fill in, and rows the
 * client could not know about (note text, filenames outside the loaded option
 * list) are appended.
 */
export function mergeServerSuggestions(localRows, serverSuggestions = [], { limit = 8 } = {}) {
  const byId = new Map(localRows.map(row => [row.id, row]));

  (serverSuggestions || []).forEach((s) => {
    const id = suggestionId(s.entity, s.column, s.value);
    const existing = byId.get(id);
    if (existing) {
      existing.count = s.count ?? existing.count;
      return;
    }
    byId.set(id, {
      id,
      entity: s.entity,
      column: s.column,
      value: s.value,
      fieldLabel: translateColumnDisplayName(s.entity, s.column, s.fieldDisplayName),
      valueLabel: translateOptionDisplayName(s.entity, s.column, s.value, s.displayName),
      subLabel: s.displaySubName || null,
      icon: s.icon || null,
      iconUrl: s.iconUrl || null,
      isActive: s.isActive,
      count: s.count ?? null,
      rank: RANK_CONTAINS,
      condition: {
        entity: s.entity,
        column: s.column,
        operator: s.operator || FILTER_OPERATORS.IN,
        values: [s.value],
      },
    });
  });

  return [...byId.values()]
    // A row the server counted at zero would filter the list down to nothing;
    // an uncounted row (null) is simply not known yet and stays.
    .filter(row => row.count !== 0)
    .sort((a, b) => a.rank - b.rank || (b.count ?? -1) - (a.count ?? -1))
    .slice(0, limit);
}
